import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { advisorApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import '@/styles/advisor-monitor.css';

type Candidate = {
  id: string;
  academia_id: string;
  full_name: string;
  program: string;
  year: string;
  supervisor: string | null;
  workplace: string | null;
  iin: string | null;
  defence_state: string;
  academic_state: string;
  review_state: string;
  points_rating: number;
  admission_year: number | null;
  topic: string | null;
};

type MonitorTab = 'overview' | 'years' | 'supervisors' | 'registry';

const yearLabel = (year: string) => {
  if (year === 'YEAR_1') return '1 курс';
  if (year === 'YEAR_2') return '2 курс';
  if (year === 'YEAR_3') return '3 курс';
  return year;
};

const dash = (value: string | null | undefined) => (value && value.trim() ? value : '—');

const COLORS = ['#41d2ff', '#49ddb7', '#e2ad3a', '#9e80ff', '#ff7664'];

export const AdvisorMonitoringPage = ({ onClose }: { onClose?: () => void }) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const close = onClose ?? (() => navigate(routes.advisor));

  const [tab, setTab] = useState<MonitorTab>('overview');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [defended, setDefended] = useState(0);
  const [byYear, setByYear] = useState<Record<string, number>>({});
  const [bySupervisor, setBySupervisor] = useState<Record<string, number>>({});
  const [supervisor, setSupervisor] = useState('all');
  const [program, setProgram] = useState('all');
  const [defense, setDefense] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // The monitoring and dossier registry share the canonical assignment projection.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await advisorApi.module('monitoring', { tab: 'overview' });
      setTotal(Number(payload.total ?? 0));
      setDefended(Number(payload.defended ?? 0));
      setByYear((payload.by_year as Record<string, number>) ?? {});
      setBySupervisor((payload.by_supervisor as Record<string, number>) ?? {});
      setCandidates((payload.candidates as Candidate[] | undefined) ?? []);
    } catch {
      notify('Не удалось загрузить мониторинг', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.body.classList.add('advisor-monitor-lock');
    return () => document.body.classList.remove('advisor-monitor-lock');
  }, []);

  const programs = useMemo(
    () =>
      [...new Set(candidates.map((item) => item.program))].sort((a, b) => a.localeCompare(b, 'ru')),
    [candidates],
  );
  const supervisors = useMemo(
    () =>
      [...new Set(candidates.map((item) => item.supervisor || '—'))].sort((a, b) =>
        a.localeCompare(b, 'ru'),
      ),
    [candidates],
  );

  const filtered = useMemo(() => {
    return candidates.filter((row) => {
      if (supervisor !== 'all' && dash(row.supervisor) !== supervisor) return false;
      if (program !== 'all' && row.program !== program) return false;
      if (defense === 'DEFENDED' && row.defence_state !== 'DEFENDED') return false;
      if (defense === 'NOT_DEFENDED' && row.defence_state === 'DEFENDED') return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        if (
          !`${row.full_name} ${row.academia_id} ${row.program} ${row.supervisor ?? ''}`
            .toLowerCase()
            .includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [candidates, defense, program, query, supervisor]);

  const yearBars = useMemo(
    () =>
      Object.entries(byYear)
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => a.year.localeCompare(b.year)),
    [byYear],
  );

  const supervisorBars = useMemo(
    () =>
      Object.entries(bySupervisor)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
    [bySupervisor],
  );

  const maxYear = Math.max(1, ...yearBars.map((item) => item.count));
  const maxSupervisor = Math.max(1, ...supervisorBars.map((item) => item.count));

  return (
    <div className="advisor-monitor-overlay advisor-monitor-root" role="dialog" aria-modal="true">
      <div className="advisor-monitor-shell">
        <header className="advisor-monitor-top">
          <button type="button" className="advisor-monitor-back" onClick={close} aria-label="Назад">
            ←
          </button>
          <div>
            <small>Мониторинг докторантов</small>
            <h1>Мониторинг докторантов</h1>
            <p>Единый реестр обучения и защит. Неизвестные поля показываются как «—».</p>
          </div>
          <button
            type="button"
            className="advisor-monitor-close"
            onClick={close}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>

        <main className="advisor-monitor-main">
          <section className="advisor-monitor-hero" style={{ '--a': '#41d2ff' } as CSSProperties}>
            <div>
              <small>Показатели контроля</small>
              <h2>Полный реестр докторантов</h2>
              <p>
                Фильтры по программам, руководителям и статусам защиты. Данные role-scoped для
                эдвайзера.
              </p>
            </div>
          </section>

          <div className="advisor-monitor-kpis">
            <div className="advisor-monitor-kpi" style={{ '--a': '#41d2ff' } as CSSProperties}>
              <small>В ВЫБОРКЕ</small>
              <b>{loading ? '—' : filtered.length}</b>
              <span>из {total}</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#49ddb7' } as CSSProperties}>
              <small>ЗАЩИЩЕНЫ</small>
              <b>{loading ? '—' : defended}</b>
              <span>по реестру</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#e2ad3a' } as CSSProperties}>
              <small>БЕЗ ЗАЩИТЫ</small>
              <b>{loading ? '—' : Math.max(0, total - defended)}</b>
              <span>в обучении</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#9e80ff' } as CSSProperties}>
              <small>РУКОВОДИТЕЛИ</small>
              <b>{supervisors.length}</b>
              <span>в контуре</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#ff7664' } as CSSProperties}>
              <small>ПРОГРАММЫ</small>
              <b>{programs.length}</b>
              <span>активных</span>
            </div>
          </div>

          <section className="advisor-monitor-panel">
            <header>
              <div>
                <h3>Мониторинг</h3>
                <p>Обзор · динамика по курсам · руководители · полный реестр</p>
              </div>
            </header>
            <div className="advisor-monitor-body">
              <div className="advisor-monitor-tabs">
                {(
                  [
                    ['overview', 'Обзор'],
                    ['years', 'По курсам'],
                    ['supervisors', 'Руководители'],
                    ['registry', 'Полный реестр'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={tab === id ? 'active' : ''}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="advisor-monitor-filters">
                <label className="advisor-monitor-field">
                  <span>РУКОВОДИТЕЛЬ</span>
                  <select
                    value={supervisor}
                    onChange={(event) => setSupervisor(event.target.value)}
                  >
                    <option value="all">Все</option>
                    {supervisors.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>ПРОГРАММА</span>
                  <select value={program} onChange={(event) => setProgram(event.target.value)}>
                    <option value="all">Все</option>
                    {programs.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="advisor-monitor-field">
                  <span>ЗАЩИТА</span>
                  <select value={defense} onChange={(event) => setDefense(event.target.value)}>
                    <option value="all">Все</option>
                    <option value="DEFENDED">Защищён</option>
                    <option value="NOT_DEFENDED">Не защищён</option>
                  </select>
                </label>
                <label className="advisor-monitor-field" style={{ gridColumn: 'span 2' }}>
                  <span>ПОИСК</span>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="ФИО, Academia ID, программа"
                  />
                </label>
              </div>

              {loading ? (
                <div className="advisor-monitor-empty">Загрузка…</div>
              ) : tab === 'years' ? (
                <div className="advisor-monitor-bars">
                  {yearBars.map((item, index) => (
                    <div key={item.year} className="advisor-monitor-bar">
                      <button type="button">{yearLabel(item.year)}</button>
                      <div className="advisor-monitor-bar-track">
                        <i
                          style={
                            {
                              '--w': `${Math.round((item.count / maxYear) * 100)}%`,
                              '--c': COLORS[index % COLORS.length],
                            } as CSSProperties
                          }
                        />
                      </div>
                      <b>{item.count}</b>
                    </div>
                  ))}
                </div>
              ) : tab === 'supervisors' ? (
                <div className="advisor-monitor-bars">
                  {supervisorBars.map((item, index) => (
                    <div key={item.name} className="advisor-monitor-bar">
                      <button type="button">{item.name}</button>
                      <div className="advisor-monitor-bar-track">
                        <i
                          style={
                            {
                              '--w': `${Math.round((item.count / maxSupervisor) * 100)}%`,
                              '--c': COLORS[index % COLORS.length],
                            } as CSSProperties
                          }
                        />
                      </div>
                      <b>{item.count}</b>
                    </div>
                  ))}
                </div>
              ) : tab === 'registry' || tab === 'overview' ? (
                <div className="advisor-monitor-table-wrap">
                  <table className="advisor-monitor-table">
                    <thead>
                      <tr>
                        <th>Докторант</th>
                        <th>Academia ID</th>
                        <th>Курс</th>
                        <th>Программа</th>
                        <th>Руководитель</th>
                        <th>ИИН</th>
                        <th>Место работы</th>
                        <th>Защита</th>
                        <th>Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <button
                              type="button"
                              className="advisor-monitor-mini"
                              onClick={() =>
                                navigate(
                                  `${routes.advisor}/doctoral_process?candidate_id=${encodeURIComponent(row.id)}`,
                                )
                              }
                            >
                              {row.full_name}
                            </button>
                          </td>
                          <td>{row.academia_id}</td>
                          <td>{yearLabel(row.year)}</td>
                          <td>{row.program}</td>
                          <td>{dash(row.supervisor)}</td>
                          <td>{dash(row.iin)}</td>
                          <td>{dash(row.workplace)}</td>
                          <td>{row.defence_state === 'DEFENDED' ? 'Защищён' : 'В обучении'}</td>
                          <td>{row.points_rating}</td>
                        </tr>
                      ))}
                      {!filtered.length ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="advisor-monitor-empty">Нет записей по фильтрам</div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
